<template>
  <div>
    <vue-headful :title="this.setPageTitle('efficiencyTestsList')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">{{ $t('efficiencyTestsList.efficiencyTestsListTitle') }}</h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="primary" @click="createNewTest">
            {{ $t('efficiencyTestsList.createNewTest') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- TABLE FILTER -->
      <b-row class="mb-4">
        <b-col>
          <table-filters wide>
            <template v-slot:search>
              <b-row>
                <b-col cols="4">
                  <label
                    class="fieldLabel"
                    for="name"
                    v-t="'efficiencyTestsList.efficiencyTestsListTable.filterText'"
                  ></label>
                  <custom-search-bar
                    :debounce="800"
                    :text-value="getSearchBarValue"
                    :placeholder="$t('efficiencyTestsList.searchByEfficiencyTest')"
                    @on-search="onSearchScoringProfile"
                  ></custom-search-bar>
                </b-col>
                <b-col cols="3">
                  <label
                    class="fieldLabel"
                    for="isin"
                    v-t="'efficiencyTestsList.efficiencyTestsListTable.isinFilterLabel'"
                  ></label>
                  <b-form-input
                    id="isin"
                    size="sm"
                    autocomplete="off"
                    :debounce="800"
                    :value="getIsinFilter"
                    :placeholder="$t('efficiencyTestsList.efficiencyTestsListTable.isinFilterPlaceholder')"
                    @update="onSearchIsin"
                  ></b-form-input>
                </b-col>
                <b-col cols="2">
                  <label
                    class="fieldLabel"
                    for="status"
                    v-t="'efficiencyTestsList.efficiencyTestsListTable.statusFilterLabel'"
                  ></label>
                  <b-form-select
                    id="status"
                    size="sm"
                    v-model="statusFilterValue"
                    :options="statusOptions"
                  ></b-form-select>
                </b-col>
                <b-col cols="3">
                  <label
                    class="fieldLabel"
                    for="outcome"
                    v-t="'efficiencyTestsList.efficiencyTestsListTable.outcomeFilterLabel'"
                  ></label>
                  <b-form-select
                    id="outcome"
                    size="sm"
                    v-model="outcomeFilterValue"
                    :options="outcomeOptions"
                  ></b-form-select>
                </b-col>
              </b-row>
            </template>
            <template v-slot:filteredItems>
              <span class="text-secondary align-middle">
                <span class="font-weight-bold">{{ getPagination.items }}</span>
                {{ $tc('efficiencyTestsList.efficiencyTestsListTable.filteredProfiles', getPagination.items) }}
              </span>
            </template>
            <template v-slot:resetButton>
              <div>
                <b-button @click="onResetFilters" variant="link" :disabled="!hasSearched" size="sm" class="ml-2">
                  <span>{{ $t('search.label.resetFilters') }} <i class="fa fa-trash small ml-1 icon-size-10"></i></span>
                </b-button>
              </div>
            </template>
          </table-filters>
        </b-col>
      </b-row>

      <template v-if="getEfficiencyTestsList.length > 0">
        <b-row class="mb-4">
          <b-col>
            <div class="ml-auto">
              <table-pagination
                :pagination="getPagination"
                :pagination-options="pagination_options"
                :pagination-text="getPaginationItemsCounter"
                :products-per-page="$t('efficiencyTestsList.perPage')"
                :pagination-class="['mb-4']"
                @on-change-per-page="onChangePerPage"
                @on-change-current-page="onChangeCurrentPage"
              >
              </table-pagination>
            </div>
          </b-col>
        </b-row>

        <!-- TABLE SCORING PROFILES -->
        <b-row class="mb-4">
          <b-col>
            <b-table
              class="list-table efficiency-tests-list-table mb-0"
              hover
              striped
              no-local-sorting
              no-sort-reset
              :fields="fields"
              :tbody-tr-class="['']"
              :items="getEfficiencyTestsList"
              @sort-changed="onSortingChanged"
            >
              <template v-slot:cell(name)="{ item }">
                <div v-if="item.name">
                  <span> {{ item.name }}</span>
                </div>
                <div v-else>
                  <span>{{ $t('efficiencyTestsList.efficiencyTestsListTable.customProfile') }}</span>
                </div>
              </template>

              <template v-slot:cell(scoringProfileModelName)="{ item }">
                <div class="model-name text-truncate" :title="item.scoringProfileModelName">
                  {{ item.scoringProfileModelName }}
                </div>
              </template>

              <template v-slot:cell(profileCoverage)="{ item }">
                <div>
                  <span class="mr-2"
                    >{{ $t('efficiencyTestsList.efficiencyTestsListTable.coverage.primaryMarket') }}:</span
                  >
                  <span>{{ item.primaryMarketIsinCount }}</span>
                </div>
                <div>
                  <span class="mr-2"
                    >{{ $t('efficiencyTestsList.efficiencyTestsListTable.coverage.secondaryMarket') }}:</span
                  >
                  <span>{{ item.secondaryMarketIsinCount }}</span>
                </div>
              </template>

              <template v-slot:cell(status)="{ item }">
                <!-- One line: the icon must not wrap under the label in a narrow status column. -->
                <div class="d-inline-flex align-items-center text-nowrap">
                  <efficiency-test-status :status="item.status"></efficiency-test-status>
                  <failure-icon
                    v-if="item.status === 'Failed'"
                    clickable
                    :type="item.lastErrorIsBlocking ? 'blocking' : 'transient'"
                    :text="
                      $tc('efficiencyTestFailures.listTooltip', item.failureCount || 0, { n: item.failureCount || 0 })
                    "
                    @click="failuresTest = item"
                  ></failure-icon>
                </div>
              </template>
              <template v-slot:cell(outcome)="{ item }">
                <efficiency-test-outcome :outcome="item.outcome"></efficiency-test-outcome>
              </template>
              <template v-slot:cell(insight)="{ item }">
                <!-- insight -->
                <router-link v-if="item.outcome" :to="goToInsight(item.scoringProfileId)">
                  <span class="mr-2">{{ $t('efficiencyTestsList.efficiencyTestsListTable.results') }}</span>
                  <i class="fas fa-long-arrow-right"></i>
                </router-link>
                <operation-progress
                  v-else-if="follower.progressOf(item.scoringProfileId)"
                  :progress="follower.progressOf(item.scoringProfileId)"
                ></operation-progress>
                <a v-else-if="executeCommand(item)" href="#" @click.prevent="confirmExecuteTest(item)">
                  <span class="mr-2">{{
                    $t('efficiencyTestsList.efficiencyTestsListTable.' + executeCommand(item))
                  }}</span>
                  <i class="fas fa-sync-alt"></i>
                </a>
              </template>
              <template v-slot:cell(actions)="{ item }">
                <!-- actions -->
                <div class="text-right">
                  <b-dropdown size="md" toggle-class="text-decoration-none button-primary-class p-3" right no-caret>
                    <template #button-content>
                      <i class="fas fa-ellipsis-v"></i>
                      <span class="sr-only">{{
                        $t('efficiencyTestsList.efficiencyTestsListTable.columns.actions')
                      }}</span>
                    </template>
                    <b-dropdown-item-button
                      v-if="isEditable(item)"
                      @click="goToEditingProfile(item.scoringProfileId, 'update')"
                    >
                      <small>{{ $t('efficiencyTestsList.efficiencyTestsListActions.update') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="goToEditingProfile(item.scoringProfileId, 'duplicate', item)">
                      <small>{{ $t('efficiencyTestsList.efficiencyTestsListActions.duplicate') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button v-if="canDelete(item)" @click="confirmDeleteTest(item)">
                      <small>{{ $t('efficiencyTestsList.efficiencyTestsListActions.delete') }}</small>
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>

        <!-- PAGINATION -->
        <b-row class="mb-4" v-if="getPagination.items > getPagination.perPage">
          <b-col cols="12">
            <div class="float-right">
              <b-pagination
                class="mb-0"
                :value="getPagination.currentPage"
                :total-rows="getPagination.items"
                :per-page="getPagination.perPage"
                @change="onChangeCurrentPage"
              >
              </b-pagination>
            </div>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <no-content
          :icon-name="'exclamation-circle'"
          :icon-class="['mb-3', 'fa-3x']"
          :text="$t('efficiencyTestsList.noContent')"
        >
        </no-content>
      </template>

      <custom-modal
        v-if="productPickerValue != null"
        :id="'efficiencyTestsProductPicker'"
        v-model="productPickerValue"
        @on-click-table-row="onProductPicked"
      />

      <efficiency-test-failures-modal
        :test="failuresTest"
        @close="failuresTest = null"
      ></efficiency-test-failures-modal>
    </b-container>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import CustomSearchBar from '../../components/EfficiencyTests/CustomSearchBar.vue'
import TablePagination from '../../components/EfficiencyTests/TablePagination.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import TableFilters from '../../components/Lib/TableFilters.vue'
import NoContent from '../../components/Lib/NoContent.vue'
import CustomModal from '../../components/Lib/CustomModal.vue'
import EfficiencyTestOutcome from '../../components/EfficiencyTests/EfficiencyTestOutcome.vue'
import EfficiencyTestStatus from '../../components/EfficiencyTests/EfficiencyTestStatus.vue'
import OperationProgress from '../../components/Lib/OperationProgress.vue'
import FailureIcon from '../../components/EfficiencyTests/FailureIcon.vue'
import EfficiencyTestFailuresModal from '../../components/EfficiencyTests/EfficiencyTestFailuresModal.vue'
import { createExecutionFollower } from '../../utils/executionFollower'
import efficiencyTestExecuteRejection from '../../mixins/efficiencyTestExecuteRejection'
import { testCommand } from '../../utils/testCommand'
import { duplicateRouteQuery } from '../../utils/efficiencyTestEditTabs'
const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers('efficiencyTestsList')
import { formatDate } from '../../utils/formatter'

export default {
  components: {
    CustomFlexHeader,
    CustomSearchBar,
    TablePagination,
    TableFilters,
    NoContent,
    CustomModal,
    EfficiencyTestOutcome,
    EfficiencyTestStatus,
    OperationProgress,
    FailureIcon,
    EfficiencyTestFailuresModal,
  },

  mixins: [efficiencyTestExecuteRejection],

  // Before the options of the component are set up: a watcher that runs at once (`immediate`) already adopts
  // the executions under way.
  beforeCreate() {
    this.follower = createExecutionFollower({ onEnded: () => this.refreshAfterExecution() })
  },

  beforeDestroy() {
    this.follower.stopAll()
    clearTimeout(this.statusRefreshTimer)
  },

  data() {
    return {
      // The Failed test whose reasons are open in the modal.
      failuresTest: null,
      pagination_options: [25, 100, 500, 1000],
      productPickerValue: null,
    }
  },
  watch: {
    // A test already in progress when the list is loaded is followed from where it is.
    getEfficiencyTestsList(items) {
      this.follower.adopt(items)
      this.scheduleStatusRefresh(items)
    },
  },

  computed: {
    ...mapGetters([
      'getPagination',
      'getEfficiencyTestsList',
      'getSearchBarValue',
      'getIsinFilter',
      'getStatusFilter',
      'getOutcomeFilter',
    ]),

    statusOptions() {
      return [{ value: null, text: this.$t('common.allMale') }].concat(
        ['Created', 'InProgress', 'WaitingResources', 'Ready', 'Failed'].map((value) => ({
          value,
          text: this.$t('efficiencyTestsList.efficiencyTestsListTable.statusOptions.' + value),
        }))
      )
    },

    outcomeOptions() {
      return [{ value: null, text: this.$t('common.allMale') }].concat(
        ['Pass', 'Fail'].map((value) => ({
          value,
          text: this.$t('efficiencyTestsList.efficiencyTestsListTable.outcomeOptions.' + value),
        }))
      )
    },

    statusFilterValue: {
      get() {
        return this.getStatusFilter
      },
      set(newVal) {
        this.setStatusFilter(newVal)
        this.getPagination.currentPage === 1 ? this.loadEfficiencyTests() : this.setPaginationCurrentPage(1)
      },
    },

    outcomeFilterValue: {
      get() {
        return this.getOutcomeFilter
      },
      set(newVal) {
        this.setOutcomeFilter(newVal)
        this.getPagination.currentPage === 1 ? this.loadEfficiencyTests() : this.setPaginationCurrentPage(1)
      },
    },

    getLoading: {
      get() {
        return this.$store.getters['efficiencyTestsCommon/getLoading']
      },
    },

    getPaginationItemsCounter: {
      get() {
        let counter = 1
        if (this.getPagination !== null && this.getPagination.items !== 0) {
          counter =
            this.getPagination.items <= this.getPagination.perPage
              ? 1
              : this.getPagination.perPage * (this.getPagination.currentPage - 1) + 1
        }

        return this.$t('efficiencyTestsList.shown', {
          start: counter,
          end: Math.min(this.getPagination.perPage * this.getPagination.currentPage, this.getPagination.items),
          of: this.getPagination.items || 0,
        })
      },
    },

    hasSearched() {
      return (
        this.getSearchBarValue.length > 0 ||
        this.getIsinFilter.length > 0 ||
        this.getStatusFilter !== null ||
        this.getOutcomeFilter !== null
      )
    },

    fields: {
      get() {
        return [
          {
            key: 'name',
            sortKey: 'Name',
            class: ['name', 'align-middle', 'font-weight-bold'],
            sortable: true,
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.name'),
          },
          {
            key: 'testedProductIsin',
            sortKey: 'Isin',
            class: ['isin', 'align-middle'],
            sortable: true,
            label: this.$t('common.isin'),
          },
          {
            key: 'scoringProfileModelName',
            sortKey: 'ProfileModel',
            class: ['profileModel', 'align-middle'],
            sortable: true,
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.scoringDefinition'),
          },
          {
            key: 'profileCoverage',
            sortable: false,
            class: ['profileCoverage', 'align-middle'],
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.coverage'),
          },
          {
            key: 'lastUpdatedBy',
            sortKey: 'LastUpdatedBy',
            class: ['user', 'align-middle'],
            sortable: true,
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.user'),
          },
          {
            key: 'lastUpdatedAt',
            sortKey: 'LastUpdatedAt',
            class: ['last-edited-at', 'align-middle'],
            formatter: (value) => {
              return value ? formatDate(value) : '-'
            },
            sortable: true,
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.lastEdit'),
          },
          {
            key: 'status',
            sortKey: 'Status',
            sortable: true,
            class: ['status', 'align-middle'],
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.status'),
          },
          {
            key: 'outcome',
            sortKey: 'Outcome',
            sortable: true,
            class: ['income', 'align-middle'],
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.outcome'),
          },
          {
            key: 'insight',
            sortable: false,
            class: ['insight', 'align-middle'],
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.insight'),
          },
          {
            key: 'actions',
            sortable: false,
            label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.actions'),
            class: ['actions', 'align-middle'],
          },
        ]
      },
    },
  },
  methods: {
    ...mapMutations([
      'setSearchBarValue',
      'setIsinFilter',
      'setStatusFilter',
      'setOutcomeFilter',
      'setPaginationPerPage',
      'setPaginationCurrentPage',
      'setSortingValue',
    ]),
    ...mapActions(['loadEfficiencyTests', 'deleteScoringProfile', 'executeEfficiencyTest']),

    onResetFilters() {
      this.setSearchBarValue('')
      this.setIsinFilter('')
      this.setStatusFilter(null)
      this.setOutcomeFilter(null)
      this.getPagination.currentPage === 1 ? this.loadEfficiencyTests() : this.setPaginationCurrentPage(1)
    },

    createNewTest() {
      this.$store.dispatch('efficiencyTestsCommon/setLoading', true)

      const payload = {
        Sf: null,
        companyProductTypes: null,
        scoringProfileIds: null,
        Sort: 'name',
        Page: 1,
        Size: 100,
      }

      this.post('/api/company/productsSearch', payload)
        .then((data) => {
          const products = Array.isArray(data.products) ? data.products : []

          this.productPickerValue = {
            title: this.$t('efficiencyTestsList.productPicker.title'),
            subtitle: this.$t('efficiencyTestsList.productPicker.subtitle'),
            subHeader: {
              number: products.length,
              label: this.$tc('efficiencyTestsList.productPicker.available', products.length),
            },
            otherInfo: {},
            placeholder: this.$t('companyProducts.searchByName'),
            items: products,
            fields: [
              { key: 'name', label: this.$t('common.product') },
              { key: 'isin', label: this.$t('common.isin') },
              { key: 'issuerCode', label: this.$t('common.issuers') },
            ],
            labelDisplayClass: ['d-inline'],
          }

          this.$nextTick(() => {
            this.$bvModal.show('efficiencyTestsProductPicker')
          })
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.$store.dispatch('efficiencyTestsCommon/setLoading', false)
        })
    },

    onProductPicked(product) {
      this.$bvModal.hide('efficiencyTestsProductPicker')
      this.$store.dispatch('efficiencyTestsCommon/setLoading', true)

      this.$store
        .dispatch('efficiencyTestsCommon/loadSelectedProduct', {
          issuerCode: product.issuerCode,
          name: product.name,
          referenceDate: product.referenceDate,
        })
        .then((selected) => {
          this.$store.dispatch('efficiencyTestsCommon/newScoringProfile')
          this.$router.push({ name: 'efficiencyTestsCreate', query: { productId: selected.productId } })
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.$store.dispatch('efficiencyTestsCommon/setLoading', false)
        })
    },

    // Reloads the list, where the test turns from in progress to its outcome.
    // A test that is running but has no operation to follow (its progress could not be recorded, or it was
    // given up on) would stay as it was: the list is read again until its status changes.
    scheduleStatusRefresh(items) {
      clearTimeout(this.statusRefreshTimer)
      const unfollowed = (items || []).some(
        (item) =>
          (item.status === 'InProgress' || item.status === 'WaitingResources') &&
          !this.follower.isTracking(item.scoringProfileId || item.id)
      )
      if (unfollowed) {
        this.statusRefreshTimer = setTimeout(() => {
          if (typeof document === 'undefined' || !document.hidden) {
            this.refreshAfterExecution()
          } else {
            this.scheduleStatusRefresh(this.getEfficiencyTestsList)
          }
        }, 10000)
      }
    },

    refreshAfterExecution() {
      return this.loadEfficiencyTests().catch((err) => {
        this.httpErrorsHandler(err)
      })
    },

    goToInsight(id) {
      const routeData = this.$router.resolve({
        name: 'efficiencyTestsDetails',
        params: { scoringProfileId: id.toString() },
      })
      // The path below the router base, as <router-link :to> expects it: `.href` already carries the
      // /{assetClass}/ prefix, which the link would then add a second time.
      return routeData.route.fullPath
    },

    // Confirmation box whose sentence carries the test name in bold: the translated sentence is
    // split on a marker put in place of {name}, then rebuilt as VNodes (no v-html, no inline styles).
    confirmWithTestName(item, messageKey, options) {
      const marker = '[[name]]'
      const [before, after] = this.$t(messageKey, { name: marker }).split(marker)
      const h = this.$createElement
      const message = h('p', { class: 'mb-0' }, [before, h('strong', item.name), after])

      return this.$bvModal.msgBoxConfirm([message], {
        cancelTitle: this.$t('common.cancel'),
        centered: true,
        ...options,
      })
    },

    confirmExecuteTest(item) {
      this.confirmWithTestName(item, 'efficiencyTestsList.modalMessages.confirmExecute', {
        title: this.$t('efficiencyTestsList.modalMessages.confirmExecuteTitle'),
        okTitle: this.$t('efficiencyTestsList.efficiencyTestsListTable.' + this.executeCommand(item)),
        okVariant: 'primary',
        cancelVariant: 'outline-primary',
      }).then((confirmed) => {
        if (confirmed) {
          this.executeTest(item.scoringProfileId)
        }
      })
    },

    executeTest(id) {
      this.$store.dispatch('efficiencyTestsCommon/setLoading', true)
      return this.executeEfficiencyTest(id)
        .then((started) => {
          this.follower.track(id, started && started.operationId)
          this.refreshAfterExecution()
        })
        .catch((err) => this.handleExecuteRejection(err))
        .finally(() => {
          this.$store.dispatch('efficiencyTestsCommon/setLoading', false)
        })
    },

    // A Failed test duplicated opens on the tab where its reasons are.
    goToEditingProfile(id, action, test) {
      this.$store.dispatch('efficiencyTestsCommon/setIsDuplicate', action === 'duplicate')
      this.$store.dispatch('efficiencyTestsCommon/setScoringProfileId', id)
      this.$router.push({
        name: 'efficiencyTestsEdit',
        params: { id: action === 'update' ? id.toString() : 'copy_' + id },
        query: action === 'duplicate' ? duplicateRouteQuery(test) : undefined,
      })
    },

    confirmDeleteTest(item) {
      this.confirmWithTestName(item, 'efficiencyTestsList.modalMessages.confirmDelete', {
        title: this.$t('efficiencyTestsList.modalMessages.confirmDeleteTitle'),
        okTitle: this.$t('common.remove'),
        okVariant: 'danger',
        cancelVariant: 'outline-secondary',
      }).then((confirmed) => {
        if (confirmed) {
          this.deleteScoringProfile(item.scoringProfileId)
            .then(() => {
              // Deleting the last row of a page beyond the first would leave an empty page.
              const { currentPage } = this.getPagination
              if (currentPage > 1 && this.getEfficiencyTestsList.length === 1) {
                this.onChangeCurrentPage(currentPage - 1)
              } else {
                return this.loadEfficiencyTests()
              }
            })
            .catch((err) => {
              this.httpErrorsHandler(err)
            })
        }
      })
    },

    onSearchScoringProfile(value) {
      this.setSearchBarValue(value)
      this.getPagination.currentPage === 1 ? this.loadEfficiencyTests() : this.setPaginationCurrentPage(1)
    },

    onSearchIsin(value) {
      this.setIsinFilter((value || '').trim())
      this.getPagination.currentPage === 1 ? this.loadEfficiencyTests() : this.setPaginationCurrentPage(1)
    },

    onChangeCurrentPage(value) {
      this.setPaginationCurrentPage(value)
      this.loadEfficiencyTests()
    },

    onChangePerPage(value) {
      this.setPaginationPerPage(value)
      if (this.getPagination.currentPage === 1) {
        this.loadEfficiencyTests()
      }
    },

    onSortingChanged(data) {
      let val = data.sortDesc ? '-' + data.sortBy : data.sortBy
      this.setSortingValue(val)
      this.getPagination.currentPage === 1 ? this.loadEfficiencyTests() : this.setPaginationCurrentPage(1)
    },

    // Only a Created test is edited in place: one that has run or failed is duplicated (testCommand).
    isEditable(item) {
      return testCommand(item).edit
    },

    executeCommand(item) {
      return testCommand(item).execute
    },
    canDelete(item) {
      return testCommand(item).delete
    },

  },

  created() {
    this.loadEfficiencyTests().catch((err) => {
      this.httpErrorsHandler(err)
    })
  },
}
</script>

<style lang="scss">
.page-link {
  background-color: #e9ecef;
}

.button-primary-class,
.button-primary-class:active {
  border: 0;
  color: var(--secondaryColor) !important;
  background-color: transparent !important;
}

.show > .button-primary-class {
  color: var(--secondaryColor) !important;
}

.page-item.disabled .page-link {
  background-color: #e9ecef;
}

.model-name {
  max-width: 240px;
}

// Scoped to this table: the same selector in the other lists is global CSS, and whichever file loads
// last would otherwise decide the column widths of all of them.
table.list-table.efficiency-tests-list-table {
  th,
  td {
    &.name {
      width: 30%;
    }
    &.profileModel {
      max-width: 240px;
    }
    &.insight {
      width: 10%;
    }
    &.actions {
      width: 3%;
    }
    &.profileCoverage {
      width: 9%;
    }
    &.user {
      width: 10%;
    }
    &.status {
      width: 7%;
    }
  }
}
</style>
